import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { ISSUED_CAPITAL_KEY } from '@/modules/issuer-dashboard/queries/charts.ts';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import { ISSUER_REGISTER_CLASS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/registers.tsx';
import {
  SHAREHOLDERS_HOLDINGS_QUERY_KEY,
  TOP_HOLDERS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import {
  CreateNavRequest,
  DeleteNavByIdRequest,
  GetNavHistoryListV1SrNavHistoryGetParams,
  GetNavsListResponse,
  GetNavsV1SrNavsGetParams,
  GetRegisterClassesWithNAVResponse,
  UpdateNavRequest,
} from 'api';

export const ISSUER_REGISTER_NAV_QUERY_KEY = 'vestra/nav-history-list';
export const ISSUER_NAV_LIST_QUERY_KEY = 'vestra/nav-list';

export const useNavHistoryListQuery = (
  payload: GetNavHistoryListV1SrNavHistoryGetParams,
  disabled = false,
  register_id: string,
) => {
  return useQuery<GetRegisterClassesWithNAVResponse>({
    queryKey: [ISSUER_REGISTER_NAV_QUERY_KEY, register_id, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getNavHistoryListV1SrNavHistoryGet(payload);
      return response.data;
    },
    // keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useIssuerNavListQuery = (payload: GetNavsV1SrNavsGetParams, disabled = false) => {
  const {
    page,
    page_size,
    register_class_id,
    active_to,
    active_from,
    created_at_to,
    created_at_from,
  } = payload;
  return useInfiniteQuery<GetNavsListResponse>({
    queryKey: [
      ISSUER_NAV_LIST_QUERY_KEY,
      page_size,
      register_class_id,
      active_to,
      active_from,
      created_at_to,
      created_at_from,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getNavsV1SrNavsGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled && !!payload.register_class_id,
  });
};

export const useNavCreateMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (payload: CreateNavRequest) => {
      const response = await withErrorHandling(api.v1.createNavV1SrNavPost(payload), {
        loading: `Creating ${getRegisterTypeStrings('nav')}...`,
        success: `${getRegisterTypeStrings('nav')} created`,
        setErrors,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      queryClient.invalidateQueries([ISSUER_NAV_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_CLASS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([SHAREHOLDERS_HOLDINGS_QUERY_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useNavUpdateMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
}) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (payload: UpdateNavRequest) => {
      const response = await withErrorHandling(api.v1.updateNavV1SrNavPatch(payload), {
        loading: `Updating ${getRegisterTypeStrings('nav')}...`,
        success: `${getRegisterTypeStrings('nav')} updated`,
        setErrors,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      queryClient.invalidateQueries([ISSUER_NAV_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_CLASS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([SHAREHOLDERS_HOLDINGS_QUERY_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useDeleteNavMutation = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  return useMutation({
    mutationFn: async (payload: DeleteNavByIdRequest) => {
      const response = await withErrorHandling(api.v1.deleteNavV1SrNavDelete(payload), {
        loading: `Deleting ${getRegisterTypeStrings('nav')}...`,
        success: `${getRegisterTypeStrings('nav')} deleted`,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      queryClient.invalidateQueries([ISSUER_NAV_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_CLASS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([SHAREHOLDERS_HOLDINGS_QUERY_KEY]);
      queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useGenerationTokenMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await withErrorHandling(
        api.v1.embeddableTokenV1UsersEmbeddableTokenPost({}),
        {
          loading: `Generating API key`,
          success: `Generated API key successful`,
        },
      );

      return response.data;
    },
    // onSuccess: () => {},
  });
};
